/**
 * Layer 7: Gödel Challenge Module — POLISHED
 * Armadillo Adamantium Security Framework
 * 
 * Responsibility: Generate challenges to distinguish humans from bots.
 * 
 * Polish additions:
 * - Accessibility-aware challenge adaptation
 * - Secondary challenge types (pattern match, timing proof, simple math)
 * - Route-specific difficulty scaling
 * - Human-testing telemetry tracking
 */

import { sha256 } from 'js-sha256';
import { GodelChallenge, GodelResponse, ChallengeType, AccessibilityProfile } from './types';

// ============================================
// WANG TILE GENERATION
// ============================================

/**
 * Generate Wang tile challenge
 * Creates a grid coloring problem that's easy for humans, hard for bots
 */
export function generateGodelChallenge(
  seed?: number,
  difficulty: 'easy' | 'medium' | 'hard' = 'medium'
): GodelChallenge {
  // Generate or use provided seed
  const challengeSeed = seed || Math.floor(Math.random() * 1000000);
  
  // Grid size based on difficulty
  const gridSize = difficulty === 'easy' ? 3 : difficulty === 'medium' ? 4 : 5;
  
  // Generate tile set (4 colors: 0, 1, 2, 3)
  const tileSet = generateTileSet(challengeSeed, gridSize);
  
  // Create challenge string (JSON of tile constraints)
  const challenge = JSON.stringify({
    seed: challengeSeed,
    gridSize,
    constraints: tileSet.constraints,
    initialColors: tileSet.initialColors
  });
  
  return {
    challenge,
    tileSet: (typeof Buffer !== 'undefined' ? Buffer.from(JSON.stringify(tileSet.colors)).toString('base64') : btoa(JSON.stringify(tileSet.colors))),
    seed: challengeSeed,
    timestamp: Date.now(),
    validFor: 300 // 5 minutes
  };
}

/**
 * Generate tile set with constraints
 */
function generateTileSet(seed: number, gridSize: number): {
  colors: number[][];
  constraints: string[];
  initialColors: Record<string, number>;
} {
  // Seeded random number generator
  let rng = seed;
  const random = () => {
    rng = (rng * 16807 + 0) % 2147483647;
    return rng / 2147483647;
  };
  
  // Generate initial grid with random colors
  const colors: number[][] = [];
  for (let i = 0; i < gridSize; i++) {
    colors[i] = [];
    for (let j = 0; j < gridSize; j++) {
      colors[i][j] = Math.floor(random() * 4);
    }
  }
  
  // Generate constraints (adjacent tiles must have different colors)
  const constraints: string[] = [];
  for (let i = 0; i < gridSize; i++) {
    for (let j = 0; j < gridSize - 1; j++) {
      constraints.push(`[${i},${j}] != [${i},${j + 1}]`);
    }
  }
  for (let i = 0; i < gridSize - 1; i++) {
    for (let j = 0; j < gridSize; j++) {
      constraints.push(`[${i},${j}] != [${i + 1},${j}]`);
    }
  }
  
  // Set some initial colors (30% of cells)
  const initialColors: Record<string, number> = {};
  for (let i = 0; i < gridSize; i++) {
    for (let j = 0; j < gridSize; j++) {
      if (random() < 0.3) {
        initialColors[`${i},${j}`] = colors[i][j];
      }
    }
  }
  
  return { colors, constraints, initialColors };
}

// ============================================
// CHALLENGE VERIFICATION
// ============================================

/**
 * Verify Gödel challenge response
 * Checks if solution satisfies all constraints
 */
export function verifyGodelResponse(
  challenge: GodelChallenge,
  response: GodelResponse
): { valid: boolean; score: number; reason?: string } {
  try {
    // Check if challenge is still valid
    const age = Date.now() - challenge.timestamp;
    if (age > challenge.validFor * 1000) {
      return { valid: false, score: 0, reason: 'Challenge expired' };
    }
    
    // Parse challenge
    const challengeData = JSON.parse(challenge.challenge);
    const { gridSize, initialColors, constraints } = challengeData;
    
    // Parse response (solution grid)
    const solutionGrid: number[][] = JSON.parse(response.challenge);
    
    // Verify grid dimensions
    if (solutionGrid.length !== gridSize) {
      return { valid: false, score: 0, reason: 'Invalid grid size' };
    }
    
    // Verify initial colors match
    for (const [key, color] of Object.entries(initialColors)) {
      const [i, j] = key.split(',').map(Number);
      if (solutionGrid[i][j] !== color) {
        return { valid: false, score: 0, reason: `Initial color mismatch at [${i},${j}]` };
      }
    }
    
    // Verify constraints
    let violations = 0;
    
    // Horizontal constraints
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize - 1; j++) {
        if (solutionGrid[i][j] === solutionGrid[i][j + 1]) {
          violations++;
        }
      }
    }
    
    // Vertical constraints
    for (let i = 0; i < gridSize - 1; i++) {
      for (let j = 0; j < gridSize; j++) {
        if (solutionGrid[i][j] === solutionGrid[i + 1][j]) {
          violations++;
        }
      }
    }
    
    // Calculate score (0-1)
    const totalConstraints = constraints.length;
    const score = Math.max(0, 1 - violations / totalConstraints);

    // Check timing (difficulty-dependent thresholds)
    // gridSize 3=easy 800-30s, 4=medium 2s-60s, 5=hard 4s-120s
    const minTimes: Record<number, number> = { 3: 800, 4: 2000, 5: 4000 };
    const maxTimes: Record<number, number> = { 3: 30000, 4: 60000, 5: 120000 };
    const humanMinTime = minTimes[gridSize] || 2000;
    const humanMaxTime = maxTimes[gridSize] || 60000;

    if (response.elapsedMs < humanMinTime) {
      return {
        valid: false,
        score: 0,
        reason: `Solved too fast (${response.elapsedMs}ms < ${humanMinTime}ms) — likely bot`
      };
    }

    if (response.elapsedMs > humanMaxTime) {
      return {
        valid: false,
        score: 0,
        reason: `Solved too slow (${response.elapsedMs}ms > ${humanMaxTime}ms) — timed out`
      };
    }
    
    // Valid if score >= 0.9 (90% constraints satisfied)
    const valid = score >= 0.9;
    
    return { valid, score };
    
  } catch (error) {
    return {
      valid: false,
      score: 0,
      reason: error instanceof Error ? error.message : 'Invalid response format'
    };
  }
}

// ============================================
// CHALLENGE UTILITIES
// ============================================

/**
 * Generate challenge hash for caching
 */
export function hashChallenge(challenge: GodelChallenge): string {
  return sha256(challenge.challenge + challenge.seed.toString());
}

/**
 * Check if challenge has been solved recently (prevent replay)
 */
export function hasBeenSolvedRecently(challengeHash: string): boolean {
  const cacheKey = `armadillo_godel_solved:${challengeHash}`;
  const solved = localStorage.getItem(cacheKey);
  
  if (!solved) return false;
  
  const solvedTime = parseInt(solved);
  const age = Date.now() - solvedTime;
  
  // Mark as solved for 1 hour
  return age < 3600000;
}

/**
 * Mark challenge as solved
 */
export function markChallengeSolved(challengeHash: string): void {
  const cacheKey = `armadillo_godel_solved:${challengeHash}`;
  localStorage.setItem(cacheKey, Date.now().toString());
}

/**
 * Clear expired solved challenges
 */
export function cleanupSolvedChallenges(): void {
  const keys = Object.keys(localStorage).filter(k => k.startsWith('armadillo_godel_solved:'));
  const oneHourAgo = Date.now() - 3600000;
  
  keys.forEach(key => {
    const solvedTime = parseInt(localStorage.getItem(key) || '0');
    if (solvedTime < oneHourAgo) {
      localStorage.removeItem(key);
    }
  });
}

/**
 * Calculate difficulty based on risk score
 */
export function getDifficultyForRisk(riskScore: number): 'easy' | 'medium' | 'hard' {
  if (riskScore < 0.3) return 'easy';
  if (riskScore < 0.7) return 'medium';
  return 'hard';
}

/**
 * Generate human-readable challenge description
 */
export function describeChallenge(challenge: GodelChallenge): string {
  const challengeData = JSON.parse(challenge.challenge);
  const gridSize = challengeData.gridSize;
  
  return `Color this ${gridSize}x${gridSize} grid so that no two adjacent cells (horizontally or vertically) have the same color. Use colors 0-3.`;
}

// ============================================
// POLISH: ACCESSIBILITY-AWARE CHALLENGE ADAPTATION
// ============================================

/**
 * Detect user accessibility needs from request signals
 */
export function detectAccessibilityProfile(req?: Request): AccessibilityProfile {
  const profile: AccessibilityProfile = {
    prefersReducedMotion: false,
    prefersHighContrast: false,
    screenReader: false,
    challengeAdaptation: 'standard',
  };
  
  if (!req) return profile;
  
  // Check for accessibility headers (set by client SDK)
  const a11yHeader = req.headers.get('x-a11y-preferences');
  if (a11yHeader) {
    try {
      const prefs = JSON.parse(a11yHeader);
      profile.prefersReducedMotion = !!prefs.reducedMotion;
      profile.prefersHighContrast = !!prefs.highContrast;
      profile.screenReader = !!prefs.screenReader;
    } catch { /* ignore parse errors */ }
  }
  
  // Determine adaptation
  if (profile.screenReader) profile.challengeAdaptation = 'audio';
  else if (profile.prefersReducedMotion) profile.challengeAdaptation = 'simplified';
  
  return profile;
}

/**
 * Select the best challenge type based on accessibility profile and context
 */
export function selectChallengeType(
  profile: AccessibilityProfile,
  routeSensitivity: 'low' | 'medium' | 'high',
  failureCount: number
): ChallengeType {
  // Screen reader users get simple math challenges
  if (profile.challengeAdaptation === 'audio') return 'simple_math';
  
  // Reduced motion users get pattern match (no animation)
  if (profile.challengeAdaptation === 'simplified') return 'pattern_match';
  
  // High failure count: fall back to simpler challenge
  if (failureCount > 3) return 'simple_math';
  
  // Sensitive routes get harder challenge types
  if (routeSensitivity === 'high') return 'wang_tile';
  if (routeSensitivity === 'medium') return 'pattern_match';
  
  // Default
  return 'wang_tile';
}

/**
 * Generate a secondary challenge type (fallback when Wang tile is unsuitable)
 */
export function generateSecondaryChallenge(
  type: ChallengeType,
  seed?: number
): { challenge: string; answer?: string; validFor: number } {
  const s = seed || Math.floor(Math.random() * 1000000);
  
  switch (type) {
    case 'pattern_match': {
      // Simple pattern: find the odd one out
      const patterns = [
        { items: ['apple', 'banana', 'car', 'orange'], answer: 'car' },
        { items: ['red', 'blue', '7', 'green'], answer: '7' },
        { items: ['cat', 'dog', 'bird', 'table'], answer: 'table' },
        { items: ['1', '2', '3', 'hello', '4'], answer: 'hello' },
      ];
      const pattern = patterns[s % patterns.length];
      return {
        challenge: JSON.stringify({ type: 'pattern_match', question: 'Which item does not belong?', items: pattern.items }),
        answer: pattern.answer,
        validFor: 30,
      };
    }
    
    case 'simple_math': {
      const a = 10 + (s % 40);
      const b = 5 + (s % 20);
      const answer = String(a + b);
      return {
        challenge: JSON.stringify({ type: 'simple_math', question: `What is ${a} + ${b}?` }),
        answer,
        validFor: 30,
      };
    }
    
    case 'timing_proof': {
      const targetDelay = 1000 + (s % 2000);
      return {
        challenge: JSON.stringify({
          type: 'timing_proof',
          instruction: `Wait approximately ${targetDelay}ms then click submit`,
          targetDelay,
          toleranceMs: 200,
        }),
        validFor: 15,
      };
    }
    
    default: // wang_tile
      return {
        challenge: JSON.stringify({ type: 'wang_tile', seed: s, gridSize: 3 }),
        validFor: 30,
      };
  }
}

/**
 * Verify a secondary challenge response
 */
export function verifySecondaryChallenge(
  type: ChallengeType,
  challenge: string,
  response: string,
  expectedAnswer?: string
): { valid: boolean; solveTimeMs?: number } {
  try {
    const data = JSON.parse(challenge);
    
    switch (type) {
      case 'pattern_match':
      case 'simple_math': {
        if (!expectedAnswer) return { valid: false };
        return { valid: response.toLowerCase().trim() === expectedAnswer.toLowerCase().trim() };
      }
      
      case 'timing_proof': {
        const elapsed = parseInt(response);
        const tolerance = data.toleranceMs || 200;
        return {
          valid: Math.abs(elapsed - data.targetDelay) <= tolerance,
          solveTimeMs: elapsed,
        };
      }
      
      default:
        return { valid: false };
    }
  } catch {
    return { valid: false };
  }
}

// ============================================
// POLISH: HUMAN-TESTING TELEMETRY
// ============================================

/**
 * Track challenge solve rates for calibration
 * Helps determine if challenges are too hard for real users
 */
const solveRateTracker = new Map<string, { attempts: number; successes: number; avgSolveMs: number }>();

export function recordChallengeOutcome(
  challengeType: ChallengeType,
  solved: boolean,
  solveTimeMs?: number
): void {
  const stats = solveRateTracker.get(challengeType) || { attempts: 0, successes: 0, avgSolveMs: 0 };
  stats.attempts++;
  if (solved) {
    stats.successes++;
    if (solveTimeMs) {
      // Running average
      stats.avgSolveMs = stats.avgSolveMs > 0
        ? (stats.avgSolveMs * (stats.successes - 1) + solveTimeMs) / stats.successes
        : solveTimeMs;
    }
  }
  solveRateTracker.set(challengeType, stats);
}

/**
 * Get solve rate telemetry for a challenge type
 */
export function getChallengeTelemetry(challengeType: ChallengeType): {
  attempts: number;
  successes: number;
  solveRate: number;
  avgSolveMs: number;
} {
  const stats = solveRateTracker.get(challengeType) || { attempts: 0, successes: 0, avgSolveMs: 0 };
  return {
    ...stats,
    solveRate: stats.attempts > 0 ? stats.successes / stats.attempts : 0,
  };
}
